import React from 'react';

import { MdRestaurant } from 'react-icons/md';
import { BsCart4 } from 'react-icons/bs';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  useHistory,
  useLocation,
  useRouteMatch,
  useParams,
} from 'react-router-dom';
import { GiHamburgerMenu } from 'react-icons/gi';
import { IoArrowBackSharp } from 'react-icons/io5';
import { debounce, isEmpty } from 'lodash';

import Barcode from 'modules/branch/services-management/pos-management/order/components/Barcode';
import { Button, FormInputSelect } from 'modules/common/input';
import { showModal, showAlert } from 'modules/common/modal';
import { toastWarning } from 'modules/common/toast';
import { PAYMENT_OPTS_ROUTES } from 'modules/branch/services-management/pos-management/Routes';
import { AUTH_TYPE } from 'modules/partial/constants';
import { removeNull } from 'modules/common/utils';
import ReceiptsV2 from 'modules/branch/services-management/waiter-management/components/ReceiptsV2';

import {
  useGetCart,
  useGetDiscount,
  useCheckoutOrder,
  useAddProduct,
} from '../../hooks';
import CartRow from './CartRow';
import Charges from '../charges';
import Discounts, {
  DEFAULT_FORM as discountDefaultForm,
} from '../discounts/Discounts';
import PaymentMethodModal from '../../modals/PaymentMethodModal';
import * as c from '../../constants';

const DEFAULT_FORM = {
  table: {},
};

const renderContent = () => (
  <div className="text-gray-500">
    <h4 className="font-semibold text-black">Clear cart list</h4>
    <p className="text-sm my-2 leading-6 tracking-wide">
      Are you sure you want to permanently delete all this cart?
    </p>
  </div>
);

function Cart({
  onClose,
  parentMatch,
  type,
  canProceed,
  withTable,
  branch,
  onContainerOpen,
  serviceType,
  orderUuid,
  locationState,
  qrKey: $qrKey,
  setCartTotal: $setCartTotal,
}) {
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const authType = React.useContext(AUTH_TYPE);
  const [showBarcode, setShowBarcode] = React.useState(false);
  const [cartTotal, setCartTotal] = React.useState(0);

  const [discounts, setDiscounts] = useGetDiscount();
  const location = useLocation();
  const history = useHistory();
  const [isCheckoutLoading, checkout] = useCheckoutOrder();
  const [isAddProductLoading, addProduct] = useAddProduct();

  const { pathname, state } = location;

  let navigation_code = pathname?.split('/');
  navigation_code = navigation_code?.[navigation_code?.length - 1];
  const isCancelPayment = PAYMENT_OPTS_ROUTES?.map(
    (item) => item?.key
  )?.includes(navigation_code);
  const { branch_uuid, merchant_uuid, key: qrKey } = useParams();

  const [cart, setCart, includes] = useGetCart();

  const merchantMatch = useRouteMatch('/store-branches/:branch_uuid');
  const adminMatch = useRouteMatch(
    branch_uuid
      ? '/merchants/:merchant_uuid/store-branches/:branch_uuid'
      : '/merchants/:merchant_uuid'
  );

  const dispatchReturn = {
    ADMIN: `${adminMatch?.url}/services`,
    MERCHANT: branch_uuid ? `${merchantMatch?.url}/services` : '/services',
    BRANCH: '/services',
  };

  const handleOnPay = () => {
    if (cart?.length <= 0) {
      toastWarning('Please select at least 1 item in the menu.');
      return;
    }

    if (orderUuid) {
      addProduct({ payload: cart, order_uuid: orderUuid }, () => {
        setCart([]);
      });
      return;
    }

    if (serviceType === 'order-taker') {
      checkout({ payload: cart, qrKey: $qrKey ?? qrKey }, () => {
        setCart([]);
      });
      return;
    }

    if (isEmpty(form?.table) && withTable && type !== 'CUSTOMER') {
      toastWarning('Please select a table first.');
      return;
    }

    if (withTable) {
      // TODO: call API, onSuccess; clear the cart
      history.push(`${parentMatch?.url}/waiter`);
      return;
    }

    if (isCancelPayment) {
      history.push(parentMatch?.url);
      return;
    }

    showModal({
      title: 'Select Payment Method',
      content: (onClose2) => (
        <PaymentMethodModal
          parentClose={onClose}
          onClose={onClose2}
          parentMatch={parentMatch}
          cartTotal={cartTotal}
          subtotal={includes?.subtotal()}
          onPay={onContainerOpen}
        />
      ),
      modalSize: 'modal-sm',
    });
  };

  const handleOnClear = () => {
    showAlert({
      content: () => renderContent(),
      onYes: (onClose2) => {
        setCart([]);
        setDiscounts(discountDefaultForm);
        onClose2();
      },
      onYesLabel: 'Confirm',
      onYesClassName: 'btn primary md w-40 rounded',
      onNoLabel: 'Cancel',
      onNoClassName: 'btn outline-light md w-40 rounded',
      buttonContainerClassName: 'flex justify-end gap-2 mt-4',
    });
  };

  React.useEffect(() => {
    $setCartTotal(cartTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$setCartTotal, JSON.stringify(cartTotal)]);

  return (
    <div className="flex flex-col flex-1 gap-3">
      {orderUuid ? null : (
        <>
          {withTable && type !== 'CUSTOMER' && (
            <div>
              <FormInputSelect
                label=""
                placeholder="Select a table"
                name="table"
                value={form?.table}
                onChange={setForm}
                loadOptions={debounce(
                  c.getTableOpts({
                    merchant_uuid,
                    params: removeNull({ branch_uuid: branch?.value }),
                  }),
                  1000
                )}
                className="border-gray-700 bg-transparent text-white text-lg"
                icon={<MdRestaurant className="h-4 w-4 mr-1" />}
                reLoadOptions={branch}
              />
            </div>
          )}
        </>
      )}

      <div className="flex relative gap-2 items-center">
        <div
          className={cn('flex gap-3 flex-1', {
            'items-start': orderUuid,
            'items-center': !orderUuid,
          })}
        >
          {serviceType === 'pos' && (
            <div className="md:hidden">
              <GiHamburgerMenu
                className="text-white h-[22px] w-[22px] cursor-pointer"
                onClick={onContainerOpen}
              />
            </div>
          )}
          <div className="font-bold text-2xl">
            {orderUuid ? (
              <div className="leading-tight">
                <div className="truncate max-w-[245px]">
                  {locationState?.data?.table_name || state?.data?.table_name}
                </div>
                <div className="text-xs font-normal text-gray-400">
                  Add more product (s){' '}
                </div>
              </div>
            ) : (
              'Orders'
            )}
          </div>
          <div
            className={cn(
              'bg-white min-w-[25px] text-black leading-none text-center rounded-full p-1 font-bold',
              {
                'mt-[3px]': orderUuid,
              }
            )}
          >
            {cart?.length}
          </div>

          {type === 'CUSTOMER' && (
            <div className="md:hidden ml-auto text-xs text-gray-400 mt-1.5">
              <button
                type="button"
                onClick={onClose}
                className="hover:underline"
              >
                ← Go Back
              </button>
            </div>
          )}

          {orderUuid && (
            <div className="ml-auto text-xs text-gray-400 mt-1.5">
              <button
                type="button"
                onClick={onClose}
                className="hover:underline"
              >
                ← Go Back
              </button>
            </div>
          )}
        </div>

        {type !== 'CUSTOMER' && serviceType === 'pos' && (
          <div className="md:hidden">
            <Button
              label={<IoArrowBackSharp className="h-4 w-4 text-white" />}
              className="btn sm hover:bg-gray-900"
              to={dispatchReturn?.[authType]}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 flex-1 overflow-y-auto py-1.5">
        {cart?.length > 0 ? (
          cart?.map((item, key) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={key}
              onClick={() =>
                includes?.onShowOrderDetails(item, {
                  type: 'UPDATE',
                  index: key,
                })
              }
              role="presentation"
              className={cn('cursor-pointer hover:bg-gray-900 p-1.5 rounded')}
            >
              <CartRow
                data={item}
                onRemove={(e) => {
                  e.stopPropagation();
                  includes?.onRemove(key);
                }}
                includes={includes}
                withPrice={type !== 'CUSTOMER'}
              />
            </div>
          ))
        ) : (
          <div className="my-auto flex items-center flex-col gap-3 text-center">
            <div className="mx-auto">
              <BsCart4 className="h-16 w-16" />
            </div>

            <div>
              <div className="font-semibold pt-3">No items in this order</div>
              <div className="!text-opacity-80 text-xxs text-gray-300">
                try adding items from the menu
              </div>
            </div>
          </div>
        )}
      </div>

      {type !== 'CUSTOMER' && (
        <ReceiptsV2
          subtotal={includes?.subtotal()}
          isDark
          setTotal={setCartTotal}
          isSmall
        />
      )}

      <div className="flex flex-col gap-3">
        {type !== 'CUSTOMER' && serviceType === 'pos' && (
          <div className="flex-1">
            <Barcode
              branch={branch}
              type={type}
              onShowBarcode={setShowBarcode}
            />
          </div>
        )}

        <div>
          {type !== 'CUSTOMER' && (
            <div
              className={cn('grid gap-3', {
                'grid-cols-2': isEmpty(discounts),
              })}
            >
              <Charges />
              <Discounts />
            </div>
          )}
        </div>

        {!showBarcode && (
          <div
            className={cn('flex items-center gap-3', {
              'flex-row-reverse': isCancelPayment,
            })}
          >
            {isCancelPayment ? (
              <></>
            ) : (
              <div>
                <Button
                  label="Clear"
                  className="btn bg-white font-semibold"
                  disabled={cart?.length <= 0}
                  onClick={handleOnClear}
                />
              </div>
            )}

            <div className="flex-1">
              <Button
                label={
                  isCancelPayment ? (
                    'Cancel Payment'
                  ) : (
                    <>
                      {serviceType === 'order-taker' ? 'Order' : 'Pay to order'}
                    </>
                  )
                }
                className={cn('btn primary font-semibold w-full', {
                  '!border-primary-500 !text-primary-500 !bg-primary-50':
                    isCancelPayment,
                })}
                onClick={canProceed(handleOnPay)}
                isLoading={isCheckoutLoading || isAddProductLoading}
                // disabled={cart?.length <= 0}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

Cart.defaultProps = {
  onClose: () => {},
  canProceed:
    (event) =>
    (...args) =>
      typeof event === 'function' ? event(args) : event,
  parentMatch: {},
  type: 'BRANCH',
  withTable: false,
  branch: {},
  onContainerOpen: () => {},
  serviceType: 'pos',
  orderUuid: '',
  locationState: {},
  qrKey: null,
  setCartTotal: () => {},
};

Cart.propTypes = {
  onClose: PropTypes.instanceOf(Function),
  canProceed: PropTypes.instanceOf(Function),
  parentMatch: PropTypes.instanceOf(Object),
  branch: PropTypes.instanceOf(Object),
  type: PropTypes.string,
  serviceType: PropTypes.string,
  withTable: PropTypes.bool,
  onContainerOpen: PropTypes.instanceOf(Function),
  setCartTotal: PropTypes.instanceOf(Function),
  orderUuid: PropTypes.string,
  locationState: PropTypes.instanceOf(Object),
  qrKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default Cart;
